// Local-mode mocks: [{ pattern, respond(body) }], first match wins.
//
// Database password management (users/password/*), the reveal flow, and the
// tenant's database settings (settings/noPolicy, settings/passwordRotation).
// These are checked before every other mock list (mockData.js), so this file
// also overrides the database users list (listUser) with rows that carry
// host_id and join with the managed accounts, plus listDbHosts.
//
// Stateful for the page session:
//   - app_rw on 10.20.0.11 starts "pending" and turns "active" after ~20 s.
//   - manage {enabled: true} queues a takeover (pending ~20 s, then active).
//   - rotate goes "rotating" for ~8 s, then active with a fresh rotatedAt.
//   - reveal stays "pending" ~4 s, then "approved" once, then "shown".
//     Revealing billing_rw is denied, to show that path.
import { MOCK_HTTP_STATUS } from "./dbPolicyMocks";

const ok = (extra = {}) => ({ code: 200, status: "success", message: "OK", ...extra });
const httpError = (status, error) => ({ [MOCK_HTTP_STATUS]: status, error });

const H = 3600e3;
const iso = (ms) => new Date(ms).toISOString();

const TAKEOVER_MS = 20e3;
const ROTATE_MS = 8e3;
const REVEAL_PENDING_MS = 4e3;

// db_hosts: the database servers. Same addresses as dbPolicyMocks' DB_HOSTS.
const HOSTS = [
  { id: 6, host_vm_ip: "10.20.0.11", port: 3306, db_type: "MySQL", agent_vm_ip: "10.20.0.2" },
  { id: 7, host_vm_ip: "10.20.0.12", port: 5432, db_type: "PostgreSQL", agent_vm_ip: "10.20.0.2" },
];
const hostById = (id) => HOSTS.find((h) => h.id === Number(id));

// Database roles per database. A role's password belongs to its server, so
// app_ro (in orders and billing, both on 10.20.0.11) is one managed password.
const DATABASES = [
  { db_id: 1, db_name: "orders", hostId: 6, roles: [["app_ro", "User"], ["app_rw", "User"], ["root", "Admin"]] },
  { db_id: 2, db_name: "billing", hostId: 6, roles: [["app_ro", "User"], ["billing_ro", "User"], ["billing_rw", "User"], ["postgres", "Admin"]] },
  { db_id: 3, db_name: "analytics", hostId: 7, roles: [["analyst", "User"], ["etl", "User"]] },
];

const created = Date.now() - 30 * 24 * H;
const USER_ROWS = DATABASES.flatMap((d, di) =>
  d.roles.map(([username, role], ri) => {
    const h = hostById(d.hostId);
    return {
      id: di * 10 + ri + 1,
      org_id: 1,
      tenant_id: 1,
      db_id: d.db_id,
      username,
      user_name: username,
      role,
      host: h.host_vm_ip,
      host_id: h.id,
      status: "Active",
      created_at: iso(created + (di * 10 + ri) * H),
      instance_id: 0,
      db_name: d.db_name,
      // dbPolicyMocks' listUser answered { username, database }.
      database: d.db_name,
    };
  }),
);

/* ------------------------------------------------------------- settings */

const settings = {
  noPolicyAction: "allow",
  passwordRotationHours: 24,
  updatedAt: iso(Date.now() - 3 * 24 * H),
  updatedBy: 1,
};
const touchSettings = () => {
  settings.updatedAt = iso(Date.now());
  settings.updatedBy = 1;
};

/* ----------------------------------------------------- managed accounts */

const key = (hostId, role) => `${Number(hostId)}|${String(role || "").trim()}`;
const now = () => Date.now();

// since: when the current pending/rotating step started.
const accounts = new Map();
const seed = (hostId, role, a) => accounts.set(key(hostId, role), { hostId, role, failures: 0, detail: "", ...a });
seed(6, "app_ro", { enabled: true, status: "active", hasPassword: true, rotatedAt: now() - 2 * H });
seed(6, "app_rw", { enabled: true, status: "pending", hasPassword: false, rotatedAt: null, since: now() });
seed(6, "root", {
  enabled: true,
  status: "failed",
  hasPassword: true,
  rotatedAt: now() - 50 * H,
  failures: 3,
  detail: "The agent couldn't log in to MySQL as root: access denied.",
});
seed(6, "billing_rw", { enabled: true, status: "active", hasPassword: true, rotatedAt: now() - 20 * 60e3 });
seed(7, "etl", { enabled: false, status: "active", hasPassword: true, rotatedAt: now() - 400 * H });

// Move pending/rotating along on read, like the agent would.
const advance = (a) => {
  if ((a.status === "pending" && now() - a.since >= TAKEOVER_MS) || (a.status === "rotating" && now() - a.since >= ROTATE_MS)) {
    a.status = "active";
    a.hasPassword = true;
    a.rotatedAt = now();
    a.failures = 0;
    a.detail = "";
  }
  return a;
};

const view = (a) => {
  advance(a);
  const h = hostById(a.hostId);
  const out = {
    hostId: a.hostId,
    host: h?.host_vm_ip || "",
    port: h?.port || 0,
    role: a.role,
    enabled: a.enabled,
    status: a.status,
    hasPassword: a.hasPassword,
    failures: a.failures,
  };
  if (a.rotatedAt) out.rotatedAt = iso(a.rotatedAt);
  if (a.enabled) out.nextDueAt = iso((a.rotatedAt || now()) + settings.passwordRotationHours * H);
  if (a.detail) out.detail = a.detail;
  return out;
};

const badAccount = (body) => {
  const role = String(body?.role || "").trim();
  if (!Number(body?.hostId) || !role) return httpError(400, "hostId and a role name are required");
  if (!hostById(body.hostId)) return httpError(404, "no such database host in this tenant");
  return null;
};

/* --------------------------------------------------------------- reveal */

const reveals = new Map();
let revealSeq = 0;
const fakePassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 24; i += 1) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
};

/* ---------------------------------------------------------------- mocks */

export const DB_VAULT_MOCKS = [
  {
    pattern: /\/users\/password\/list(\?|$)/,
    respond: () => ok({ tenantId: 1, accounts: [...accounts.values()].map(view) }),
  },
  {
    pattern: /\/users\/password\/manage(\?|$)/,
    respond: (body = {}) => {
      const bad = badAccount(body);
      if (bad) return bad;
      if (typeof body.enabled !== "boolean") return httpError(400, "enabled is required");
      const role = String(body.role).trim();
      const k = key(body.hostId, role);
      const a = accounts.get(k) || { hostId: Number(body.hostId), role, failures: 0, detail: "", hasPassword: false, rotatedAt: null };
      a.enabled = body.enabled;
      if (body.enabled) {
        a.status = "pending";
        a.since = now();
      }
      accounts.set(k, a);
      return ok({ hostId: Number(body.hostId), role, enabled: body.enabled });
    },
  },
  {
    pattern: /\/users\/password\/rotate(\?|$)/,
    respond: (body = {}) => {
      const bad = badAccount(body);
      if (bad) return bad;
      const role = String(body.role).trim();
      const a = accounts.get(key(body.hostId, role));
      if (!a || !a.enabled) return httpError(404, "this role's password is not managed; turn management on first");
      a.status = "rotating";
      a.since = now();
      return ok({ hostId: Number(body.hostId), role, queued: true });
    },
  },
  {
    pattern: /\/users\/password\/reveal\/result(\?|$)/,
    respond: (body = {}) => {
      const r = reveals.get(String(body?.revealId || ""));
      if (!r) return httpError(404, "no such reveal");
      if (now() - r.at < REVEAL_PENDING_MS) return ok({ status: "pending" });
      if (r.role === "billing_rw") return ok({ status: "denied" });
      if (r.consumed) return ok({ status: "shown" });
      r.consumed = true;
      return ok({ status: "approved", password: fakePassword(), showSeconds: 60, rerotateAt: iso(now() + H) });
    },
  },
  {
    pattern: /\/users\/password\/reveal(\?|$)/,
    respond: (body = {}) => {
      const bad = badAccount(body);
      if (bad) return bad;
      const role = String(body.role).trim();
      const a = accounts.get(key(body.hostId, role));
      if (!a || !a.enabled || !a.hasPassword) return httpError(404, "this role's password is not managed");
      revealSeq += 1;
      const revealId = `rv-${revealSeq}-${now().toString(36)}`;
      reveals.set(revealId, { role, at: now(), consumed: false });
      return ok({ revealId });
    },
  },

  {
    pattern: /\/settings\/passwordrotation\/get(\?|$)/,
    respond: () =>
      ok({ tenantId: 1, passwordRotationHours: settings.passwordRotationHours, updatedAt: settings.updatedAt, updatedBy: settings.updatedBy }),
  },
  {
    pattern: /\/settings\/passwordrotation(\?|$)/,
    respond: (body = {}) => {
      const h = body?.passwordRotationHours;
      if (typeof h !== "number" || !Number.isInteger(h) || h < 1 || h > 8760) {
        return httpError(400, "passwordRotationHours must be a whole number of hours from 1 to 8760");
      }
      settings.passwordRotationHours = h;
      touchSettings();
      return ok({ tenantId: 1, passwordRotationHours: h, updatedAt: settings.updatedAt, updatedBy: settings.updatedBy });
    },
  },
  {
    pattern: /\/settings\/nopolicy\/get(\?|$)/,
    respond: () => ok({ tenantId: 1, noPolicyAction: settings.noPolicyAction, updatedAt: settings.updatedAt, updatedBy: settings.updatedBy }),
  },
  {
    pattern: /\/settings\/nopolicy(\?|$)/,
    respond: (body = {}) => {
      const v = String(body?.noPolicyAction || "").toLowerCase();
      if (!["allow", "mfa", "deny"].includes(v)) return httpError(400, "noPolicyAction must be allow, mfa or deny");
      settings.noPolicyAction = v;
      touchSettings();
      return ok({ tenantId: 1, noPolicyAction: v, updatedAt: settings.updatedAt, updatedBy: settings.updatedBy });
    },
  },

  // Database → Users list, with host_id so rows join with the accounts above.
  // Honors the Database / User / Role filters and limit + page_id.
  {
    pattern: /\/listuser(\?|$)/,
    respond: (body = {}) => {
      const f = (name) => (body.filters || []).find((x) => x?.filterParameter === name)?.filterValue;
      const db = f("Database");
      const user = f("User");
      const role = f("Role");
      const rows = USER_ROWS.filter((r) => (!db || r.db_name === db) && (!user || r.username === user) && (!role || r.role === role));
      const limit = Number(body.limit) > 0 ? Number(body.limit) : 10;
      const page = Number(body.page_id) > 0 ? Number(body.page_id) : 1;
      const data = rows.slice((page - 1) * limit, page * limit);
      return ok({ data, total_count: rows.length, totalCount: rows.length, limit, page_id: page, total_pages: Math.ceil(rows.length / limit) });
    },
  },
  {
    pattern: /\/listdbhosts(\?|$)/,
    respond: () => ok({ code: "200", data: HOSTS.map((h) => ({ ...h, org_id: 1, tenant_id: 1, status: "Active" })), total_count: HOSTS.length }),
  },
  { pattern: /\/getuniquedatabasenames(\?|$)/, respond: () => ok({ db_names: DATABASES.map((d) => `${d.db_name} (${hostById(d.hostId).host_vm_ip})`) }) },
  { pattern: /\/getuniqueusernames(\?|$)/, respond: () => ok({ usernames: [...new Set(USER_ROWS.map((r) => r.username))] }) },
];
